import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCommon } from "../context/CommonContext";
import { useLanguage } from "../context/LanguageProvider";

const IATDir = () => {
    // React Router hook to programmatically navigate between routes
    const navigate = useNavigate();

    // Pull the translation function from the language context
    // translation(key) will return a language-specific string (possibly with HTML)
    const { translation } = useLanguage();

    // Amount of time (milliseconds) before user can proceed
    const continueTimer = 5000;

    // State to track when the user is allowed to continue (e.g., by pressing space)
    const [canContinue, setCanContinue] = useState(false);

    // From global/common context, access the current "sample mode"
    // Determines which instructional text variant should be shown
    const { sampleMode } = useCommon();

    // Side effect: set up timer and keyboard event listener
    useEffect(() => {
        // Handler for when a key is pressed
        const handleKeyPress = (event) => {
            // Only respond if:
            //  1. User is allowed to continue (timer has elapsed)
            //  2. The pressed key is the Space bar (code "Space")
            if (canContinue && event.code === "Space") {
                // Navigate to the next route in the experiment
                navigate("/playingAroma");
            }
        };
        
        // Add the keydown listener to the entire window
        window.addEventListener("keydown", handleKeyPress);
    
        // Declare a variable for the timer ID
        let timer;
        if (!canContinue) {
            // Start a timer to enable continuation after "continueTimer" ms
            timer = setTimeout(() => {
                setCanContinue(true);
            }, continueTimer);
        }
    
        // Cleanup function when dependency array changes or component unmounts
        return () => {
            window.removeEventListener("keydown", handleKeyPress); // remove keyboard listener
            clearTimeout(timer); // cancel pending timer if it exists
        };
    }, [canContinue]); // re-run if `canContinue` changes
    
    // CONDITIONAL RENDERING: Select which instructions appear based on `sampleMode`
    // If NOT using Visual Analog Scale ("VAS") mode:
    if (sampleMode !== "VAS") {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 relative">
                {/* Title heading for step indication is commented out (may be toggled on for debugging/design) */}
                
                {/* Instructional heading (allows HTML from translation strings) */}
                <h6
                    className="text-3xl text-gray-800 mb-6 text-left w-full max-w-6xl"
                    dangerouslySetInnerHTML={{
                        __html: translation("IATDir_Non_VAS_sampleInstructions")
                    }}
                />

                {/* Ordered points for the participant — pulled from translation keys */}
                <ul className="list-disc mb-6 w-full max-w-6xl pl-2 text-left text-2xl ml-12">
                    <li>{translation("IATDir_Non_VAS_spacebarInstructions")}</li>
                    <li>{translation("IATDir_noPressInstructions")}</li>
                    <li>{translation("IATDir_note")}</li>
                    <li>{translation("IATDir_accuracyReminder")}</li>
                </ul>

                {/* "Begin" prompt */}
                <h6 className="text-4xl text-gray-800 mb-6 text-left w-full max-w-6xl">
                    {translation("IATDir_begin")}
                </h6>

                {/* Conditional prompt to press spacebar — displays only when `canContinue` is true */}
                {canContinue && (
                    <p className="absolute bottom-20 text-4xl text-gray-800 mt-4">
                        {translation("IATDir_pressContinue")}
                    </p>
                )}
            </div>
        );

    // ELSE: VAS mode branch
    } else {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100 relative">
                {/* Same layout, but translation keys are for VAS-specific instructions */}
                
                <h6
                    className="text-3xl text-gray-800 mb-6 text-left w-full max-w-6xl"
                    dangerouslySetInnerHTML={{
                        __html: translation("IATDir_VAS_aromaInstructions")
                    }}
                />
                
                <ul className="list-disc mb-6 w-full max-w-6xl pl-2 text-left text-2xl ml-12">
                    <li>{translation("IATDir_VAS_spacebarInstructions")}</li>
                    <li>{translation("IATDir_noPressInstructions")}</li>
                    <li>{translation("IATDir_note")}</li>
                    <li>{translation("IATDir_accuracyReminder")}</li>
                </ul>

                <h6 className="text-4xl text-gray-800 mb-6 text-left w-full max-w-6xl">
                    {translation("IATDir_begin")}
                </h6>

                {canContinue && (
                    <p className="absolute bottom-20 text-4xl text-gray-800 mt-4">
                        {translation("IATDir_pressContinue")}
                    </p>
                )}
            </div>
        );
    }
};

export default IATDir;